import { useEffect } from 'react';
import { selectFocused, useWindowStore } from '@/wm/store';
import type { WindowState } from '@/wm/types';
import { buildDeepLink } from './deepLink';

/** Flip to `false` to leave the address bar alone entirely. */
export const SYNC_URL: boolean = true;

/**
 * Focus can change several times inside one gesture — close a dialog, focus
 * its parent, click through to a third window — and Safari rate-limits
 * `replaceState` hard. Coalesce.
 */
const DEBOUNCE_MS = 120;

/**
 * Mirrors the focused window into the address bar, so whatever a visitor is
 * looking at can be copied as a link.
 *
 * Always `replaceState`, never `pushState`: the Back button belongs to the
 * browser. Turning it into "close the top window" is the kind of hijacking
 * that makes a site feel broken. The one thing that does borrow it is
 * `backButton.ts`, and only for the window that is itself a browser.
 *
 * The store is subscribed imperatively rather than through the React hook.
 * The focused window's object identity changes on every frame of a drag, so a
 * reactive subscription here would re-render the entire desktop 60 times a
 * second to produce a URL that never changed.
 */
export function useDeepLinkSync(): void {
  useEffect(() => {
    if (!SYNC_URL || typeof window === 'undefined') return;

    const here = () => window.location.pathname + window.location.search;

    // No window focused: back to the bare path, so a reload lands on a clean
    // desktop rather than re-opening whatever was last looked at.
    const linkFor = (focused: WindowState | undefined) =>
      focused ? buildDeepLink(focused.appId, focused.args) : window.location.pathname;

    let timer = 0;
    /** The URL that is either live or already queued — dedupes the churn. */
    let pending = here();

    const schedule = (target: string) => {
      if (target === pending) return;
      pending = target;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        try {
          // The entry's own state is carried over rather than cleared: this
          // may be the spare `backButton.ts` pushed, and that is marked by
          // its state.
          window.history.replaceState(window.history.state, '', target);
        } catch {
          // Rate-limited, or a sandboxed context with no history access. A
          // stale URL is not worth taking the desktop down over.
          pending = here();
        }
      }, DEBOUNCE_MS);
    };

    // A deep link that opened a window before this effect ran still needs its
    // URL normalised (dropping `?noboot=1`, folding the `#open=` alias).
    schedule(linkFor(selectFocused(useWindowStore.getState())));

    const unsubscribe = useWindowStore.subscribe((state) => {
      schedule(linkFor(selectFocused(state)));
    });

    return () => {
      window.clearTimeout(timer);
      unsubscribe();
    };
  }, []);
}
